import { UseMutationOptions, useMutation, useQueryClient } from 'react-query';

import { fetchJson } from '@zapier/toolbox-browser-fetch';

import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import { useNotify } from 'app/developer-v3/hooks';
import { getQueryKey } from './useQueryPublishingForm';

import { formatPublishingForm } from './utils';

import type { PublishingForm } from './types';

const MUTATION_KEY = 'mutate-update-publishing-form';

export function formatResponseBody(body: any): PublishingForm {
  return formatPublishingForm(body);
}

function formatToRequestBody(
  publishingForm: PublishingForm,
  implementationId: number
) {
  const {
    id,
    // Integration readiness & API ownership.
    hasApiOwnership,
    apiEndpointsUsed,
    ownsAllEndpoints,
    hasPermissionForEndpoints,
    usesProductionEndpoints,
    paidApi,
    // Test account for us.
    testAccountUsername,
    testAccountPassword,
    testAccountOnlySupportsSso,
    ssoTestAccountCreated,
    testAccountNotes,
    // App details.
    hasBeenLaunched,
    isReplacementIntegration,
    alreadyManagingSimilarIntegration,
    linkToOtherIntegrations,
    newIntegrationRationale,
    homepageUrl,
    apiDocsUrl,
    apiDocumentationNotes,
    primaryColor,
    twitterUsername,
    // Contact details.
    marketingContactEmail,
    technicalContactEmail,
    expectedResponseTime,
    // Compliance & platform rules.
    companyBaseCountry,
    transmitsSensitiveValues,
    collectsFinancialInfo,
    usesThirdPartyApis,
    permissionForThirdPartyApis,
    usesZapierBranding,
    usesCurrentZapierBranding,
  } = publishingForm;

  return {
    form: {
      ...(id ? { id: String(id) } : {}),
      // Integration readiness & API ownership.
      has_api_ownership: hasApiOwnership,
      api_endpoints_used: apiEndpointsUsed,
      owns_all_endpoints: ownsAllEndpoints,
      has_permission_for_endpoints: hasPermissionForEndpoints,
      uses_production_endpoints: usesProductionEndpoints,
      paid_api: paidApi,
      // Test account for us.
      test_account_username: testAccountUsername,
      test_account_password: testAccountPassword,
      test_account_only_supports_sso: testAccountOnlySupportsSso,
      sso_test_account_created: ssoTestAccountCreated,
      test_account_notes: testAccountNotes,
      // App details.
      has_been_launched: hasBeenLaunched,
      is_replacement_integration: isReplacementIntegration,
      already_managing_similar_integration: alreadyManagingSimilarIntegration,
      link_to_other_integrations: linkToOtherIntegrations,
      new_integration_rationale: newIntegrationRationale,
      homepage_url: homepageUrl,
      api_docs_url: apiDocsUrl,
      api_documentation_notes: apiDocumentationNotes,
      primary_color: primaryColor,
      twitter_username: twitterUsername,
      // Contact details.
      marketing_contact_email: marketingContactEmail,
      technical_contact_email: technicalContactEmail,
      expected_response_time: expectedResponseTime,
      // Compliance & platform rules.
      company_base_country: companyBaseCountry,
      transmits_sensitive_values: transmitsSensitiveValues,
      collects_financial_info: collectsFinancialInfo,
      uses_third_party_apis: usesThirdPartyApis,
      permission_for_third_party_apis: permissionForThirdPartyApis,
      uses_zapier_branding: usesZapierBranding,
      uses_current_zapier_branding: usesCurrentZapierBranding,
    },
    implementationId,
  };
}

type Args = { implementationId: number; publishingForm: PublishingForm };

function useMutationPublishingForm(
  args: { integrationId: number },
  options?: UseMutationOptions<
    void,
    Error,
    Args,
    { publishingForm: PublishingForm }
  >
) {
  const { integrationId } = args;
  const notify = useNotify();

  const queryClient = useQueryClient();
  const queryKey = getQueryKey({ integrationId });
  const mutationKey = MUTATION_KEY;
  const url = new URL(
    `api/platform/cli/apps/${encodeURIComponent(
      integrationId
    )}/publishing-form`,
    ZAPIER_APP_BASE_URL
  );

  const mutation = useMutation({
    mutationKey,
    async mutationFn({ implementationId, publishingForm }: Args) {
      try {
        const response = await fetchJson(url.href, {
          body: JSON.stringify(
            formatToRequestBody(publishingForm, implementationId)
          ),
          method: 'PATCH',
        });
        return response;
      } catch (error) {
        throw formatResponseBody(JSON.parse(error.responseText));
      }
    },
    onError: (error: any, _variables) => {
      notify.failure(
        'There was a problem updating your review-request form. Please check the form values or try again.',
        mutationKey
      );
      console.error(JSON.stringify(error));
    },
    onSuccess: () => {
      notify.success('Your review-request form is updated.');
      queryClient.invalidateQueries(queryKey);
    },
    ...options,
  });

  return { ...mutation, mutationKey };
}

export default useMutationPublishingForm;
